import { BreakpointObserver } from '@angular/cdk/layout';
import {
  AfterViewInit,
  Component,
  Inject,
  Injector,
  OnDestroy,
  inject,
  signal,
  DestroyRef,
  ViewChild,
  computed
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  NavigationStart,
  Router,
  RouterModule
} from '@angular/router';
import { GlobalFooterModule } from '@delon/abc/global-footer';
import {
  ReuseTabComponent,
  ReuseTabModule,
  ReuseTabService
} from '@delon/abc/reuse-tab';
import { DA_SERVICE_TOKEN, ITokenService } from '@delon/auth';
import { MenuService, SettingsService, User, _HttpClient } from '@delon/theme';
import {
  LayoutDefaultModule,
  LayoutDefaultOptions
} from '@delon/theme/layout-default';
import { SettingDrawerModule } from '@delon/theme/setting-drawer';
import { ThemeBtnModule } from '@delon/theme/theme-btn';
import {
  SOFTWARE_DEFAULT_NAME,
  SOFTWARE_DEFAULT_VERSION,
  copyrightCompanyName
} from '@core';
import { Store } from '@ngrx/store';
import { NzSafeAny } from 'ng-zorro-antd/core/types';
import { filter, map, Subscription, tap } from 'rxjs';
import { toggleLockAction } from 'src/app/actions/app';
import { PhEisMenuModeService } from 'src/app/core/services/ph-eis-menu-mode.service';
import { PhEisService } from 'src/app/core/services/ph-eis.service';
import { StartupService } from 'src/app/core/startup/startup.service';
import { AuthService } from 'src/app/core/util/auth.service';
import { KeyboardShortcutsDirective } from 'src/app/shared/directives/keyboard-shortcuts.directive';
import { LicenseService } from 'src/app/shared/services/license.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { SharedModule } from 'src/app/shared/shared.module';
import {
  N3EIS_LOGO_URL,
  resolveProductBrand
} from 'src/app/shared/utils/product-brand';
import { PageHowToComponent } from 'src/app/shared-feature/components/page-how-to/page-how-to.component';
import { QBotReportAnalyzerComponent } from 'src/app/shared-feature/components/ai-widgets/q-bot-report-analyzer/q-bot-report-analyzer.component';

import { QbotWidgetService } from '../../routes/ai/_providers/qbot-widget.service';
import { AppNavComponent } from './nav/nav.component';
import { HeaderChatbotComponent } from './widgets/chatbot/chatbot.component';
import { HeaderFinancialAdvisorComponent } from './widgets/financial-advisor/financial-advisor.component';
import { HeaderCompanyWidgetComponent } from './widgets/header-company-widget/header-company-widget.component';
import { InitSampleComponent } from './widgets/init-sample/init-sample.component';
import { LicenseNotificationComponent } from './widgets/license-notification/license-notification.component';
import { HeaderMarketplaceComponent } from './widgets/marketplace.component';
import { MfaPromptComponent } from './widgets/mfa-prompt/mfa-prompt.component';
import { HeaderMobileDropdownNavComponent } from './widgets/mobile-dropdown-nav.component';
import { HeaderNotifyComponent } from './widgets/notify/notify.component';
import { PromptCardRunnerComponent } from './widgets/prompt-card-runner/prompt-card-runner.component';
import { HeaderQuinnyAIComponent } from './widgets/quinny-ai/quinny-ai.component';
import { HeaderSearchComponent } from './widgets/search.component';
import { HeaderSupportComponent } from './widgets/support.component';
import { HeaderTourComponent } from './widgets/tour.component';
import { TrialBannerComponent } from './widgets/trial-banner/trial-banner.component';
import { HeaderUserComponent } from './widgets/user.component';

@Component({
  selector: 'layout-basic',
  styleUrls: ['./basic.component.less'],
  templateUrl: './basic.component.html',
  standalone: true,
  imports: [
    SharedModule,
    RouterModule,
    LayoutDefaultModule,
    SettingDrawerModule,
    ThemeBtnModule,
    GlobalFooterModule,
    ReuseTabModule,
    KeyboardShortcutsDirective,
    HeaderCompanyWidgetComponent,
    HeaderSearchComponent,
    HeaderQuinnyAIComponent,
    HeaderChatbotComponent,
    HeaderTourComponent,
    HeaderNotifyComponent,
    HeaderMarketplaceComponent,
    HeaderSupportComponent,
    HeaderMobileDropdownNavComponent,
    HeaderUserComponent,
    AppNavComponent,
    TrialBannerComponent,
    PageHowToComponent,
    InitSampleComponent,
    HeaderFinancialAdvisorComponent,
    LicenseNotificationComponent,
    MfaPromptComponent,
    PromptCardRunnerComponent,
    QBotReportAnalyzerComponent
  ]
})
export class LayoutBasicComponent implements AfterViewInit, OnDestroy {
  public menuSrv = inject(MenuService);
  private readonly receiveMsg$: Subscription | undefined;
  private readonly reuseTabChangeSubscription?: Subscription;
  private readonly routerEventsSubscription?: Subscription;

  readonly #breakpointObserver = inject(BreakpointObserver);
  @ViewChild('chatbot') chatbot!: HeaderChatbotComponent;

  readonly isMobile = toSignal(
    this.#breakpointObserver.observe(['(max-width: 480px)']).pipe(
      map(result => result.matches),
      tap(matches => {
        if (matches) this.settings.layout.collapsed = true;
      })
    ),
    { initialValue: false }
  );

  readonly isTablet = toSignal(
    this.#breakpointObserver
      .observe(['(min-width: 481px) and (max-width: 1200px)'])
      .pipe(
        map(result => result.matches),
        tap(matches => {
          if (matches) this.settings.layout.collapsed = true;
        })
      ),
    { initialValue: false }
  );

  readonly isMobileOrTablet = toSignal(
    this.#breakpointObserver.observe(['(max-width: 1200px)']).pipe(
      map(result => result.matches),
      tap(matches => {
        if (matches) this.settings.layout.collapsed = true;
      })
    ),
    { initialValue: false }
  );

  readonly isDesktop = computed(() => !this.isMobileOrTablet());

  interval$: NzSafeAny;
  count = 0;
  progress = 0;
  options: LayoutDefaultOptions = {
    logoExpanded: `https://cdn.qne.cloud/qca/assets/img/n3/logo-expanded.png`,
    logoLink: '/dashboard',
    logoCollapsed: `https://cdn.qne.cloud/qca/assets/img/n3/logo.webp`
  };
  searchToggleStatus = signal(false);
  showSettingDrawer = false; //!environment.production;
  licenseText = '';
  isSmallScreen = false; //window.innerWidth < 736, and use to assign class to search button in header

  get user(): User {
    return this.settings.user;
  }

  onRequestSearch() {
    this.searchToggleStatus.set(!this.searchToggleStatus());
  }

  onOpenChatbot() {
    this.chatbot.toggleChatbot();
  }

  onAISupport() {
    this.chatbot.openAiAssistant();
  }

  get company() {
    let company = this.settings.user['company'] ?? { isSample: true };
    return company;
  }

  get country() {
    return this.settings.user['company']?.country;
  }

  get companyName() {
    return copyrightCompanyName(this.country);
  }

  get QNELink() {
    if (this.country === 'PH') {
      return 'https://qne.com.ph/';
    }
    return 'https://www.qne.com.my/';
  }

  get academyLink() {
    if (this.country === 'MY') return 'https://academy.qne.cloud/courses/qcamy';

    return 'https://academy.qne.cloud';
  }

  get websiteLink() {
    if (this.country === 'PH') return 'https://qne.cloud/ph/';

    return 'https://qne.cloud/my/';
  }

  get youtubeLink() {
    if (this.country === 'PH') return 'https://www.youtube.com/@QNESoftwarePH';

    return 'https://www.youtube.com/@QnESoftwareChannel';
  }

  destroyRef = inject(DestroyRef);
  showPremiumFeatureModal = signal<boolean>(true);
  resetPremiumFeatureModal() {
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.showPremiumFeatureModal.set(true);
      });
  }

  qbotWidgetService = inject(QbotWidgetService);
  readonly menuMode = inject(PhEisMenuModeService);
  readonly phEis = inject(PhEisService);
  constructor(
    private readonly injector: Injector,
    private readonly settings: SettingsService,
    private readonly router: Router,
    private readonly http: _HttpClient,
    private readonly authService: AuthService,
    @Inject(DA_SERVICE_TOKEN) private readonly tokenService: ITokenService,
    private readonly notifySrv: NotificationService,
    private readonly reuseTabService: ReuseTabService,
    private readonly store: Store<{ app: { isLocked: boolean } }>,
    private readonly startup: StartupService
  ) {
    this.resetPremiumFeatureModal();

    const sessionToken = this.tokenService.get() as Record<
      string,
      unknown
    > | null;
    if (
      sessionToken?.['partnerSubscriptionSession'] &&
      sessionToken?.['tenantId']
    ) {
      void this.router.navigate(['/full/partner/subscription/manage'], {
        queryParams: { tid: sessionToken['tenantId'] }
      });
    }

    // `qi` (eintegrator) has always pointed at the same asset as the N3 portal,
    // so both branches share one value; only a n3eis entry host differs (WI 35087).
    this.options.logoExpanded = resolveProductBrand().isN3Eis
      ? N3EIS_LOGO_URL
      : `https://cdn.qne.cloud/qca/assets/img/n3/logo-expanded.png`;

    if (this.company.bannerLogo)
      this.options.logoExpanded = this.company.bannerLogo;
    if (this.company.squareLogo)
      this.options.logoCollapsed = this.company.squareLogo;

    // retrieve server build version
    this.http.get('api/app/BuildVer?_silent=true').subscribe(res => {
      this._build = res;
    });

    if (this.company.isSample) {
      this.progress = this.company.initProgress;
    }

    this.reuseTabService.keepingScroll = true;

    // Persist and restore query parameters per reuse tab
    const tabQueryStore = new Map<string, string>();
    (this as any)._tabQueryStore = tabQueryStore;
    (window as any).__tabQueryStore = tabQueryStore;

    // Allow other components to request clearing a specific path's saved query
    window.addEventListener('qpa-clear', (ev: any) => {
      const path = ev?.detail?.path as string | undefined;
      if (path) tabQueryStore.delete(path);
    });
    let restoringQuery = false;
    const dashboardUrl = '/dashboard';

    const normalizeHomeTabUrl = (url: string | null | undefined): boolean => {
      const path = (url ?? '').split('?')[0].trim();
      if (path !== '' && path !== '/') return false;
      this.reuseTabService.replace(dashboardUrl);
      return true;
    };

    const tryRestoreQuery = () => {
      try {
        const currentPath = this.router.url.split('?')[0];
        const savedQuery = tabQueryStore.get(this.reuseTabService.curUrl);
        const hasQuery = this.router.url.includes('?');
        if (
          !hasQuery &&
          savedQuery &&
          this.reuseTabService.curUrl === currentPath &&
          !restoringQuery
        ) {
          restoringQuery = true;
          this.router
            .navigateByUrl(`${currentPath}?${savedQuery}`)
            .finally(() => (restoringQuery = false));
        }
      } catch (error) {
        // Silently ignore errors if injector is destroyed (component may be destroyed)
        if (
          error instanceof Error &&
          error.message.includes('Injector has already been destroyed')
        ) {
          return;
        }
        // Re-throw other errors
        throw error;
      }
    };

    // Save query params on every navigation end
    this.routerEventsSubscription = this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(
          event =>
            event instanceof NavigationStart || event instanceof NavigationEnd
        )
      )
      .subscribe(event => {
        if (event instanceof NavigationStart) {
          const nextUrl = event.url.split('?')[0].trim();
          if (
            nextUrl === '' ||
            nextUrl === '/' ||
            nextUrl === '/#' ||
            nextUrl === '#' ||
            nextUrl === '/#/' ||
            nextUrl === '#/'
          ) {
            this.router.navigateByUrl(dashboardUrl, { replaceUrl: true });
            return;
          }
        }

        const [path, query] = this.router.url.split('?');
        if (normalizeHomeTabUrl(path)) return;
        if (query && query.length) {
          tabQueryStore.set(path, query);
        }
        // When navigating to a tab URL without query, try restore
        tryRestoreQuery();
      });

    // When switching tabs, try restore the tab's saved query
    this.reuseTabChangeSubscription = this.reuseTabService.change
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (normalizeHomeTabUrl(this.reuseTabService.curUrl)) return;
        tryRestoreQuery();
      });
  }

  _build: string | null = null;
  // replaced by birSystemVersion
  get build() {
    if (this.company?.birSystemVersion) return this.company.birSystemVersion;
    if (this._build) return this._build;
    return '0.0.0.0';
  }

  /**
   * Footer product name. A `n3eis` entry host is its own product (WI 35087).
   * Otherwise the BIR e-Invoice plan (slim menu, ability {@code view.n3eis}) is
   * marketed as "N3 AI Accounting e-Invoicing Solution" (WI 34968), and every
   * other menu shows the standard N3 AI Accounting branding.
   */
  get softwareName() {
    const brand = resolveProductBrand();
    if (brand.isN3Eis) return brand.name;

    return this.phEis.isSlimMenu
      ? `${SOFTWARE_DEFAULT_NAME} e-Invoicing Solution`
      : SOFTWARE_DEFAULT_NAME;
  }

  get buildDisplay() {
    // BIR System Version (company profile) stays authoritative for PH tenants;
    // the e-Invoicing Solution shows it too, falling back to v1.0.0.
    const birVersion = this.company?.birSystemVersion?.trim();
    if (birVersion) return `${this.softwareName} ${birVersion}`;

    return `${this.softwareName} ${SOFTWARE_DEFAULT_VERSION}`;
  }

  get currentYear() {
    return new Date().getFullYear();
  }

  get copyrightYear() {
    return `2022-${this.currentYear}`;
  }

  ngAfterViewInit(): void {
    this.loadLicenseText();
    this.getScreenSize();
    // Patch iconfont symbols (Banking/Marketplace) so selected icon color works
    setTimeout(() => this.startup.patchIconfontSymbolFills(), 1500);
  }

  ngOnDestroy(): void {
    if (this.receiveMsg$) this.receiveMsg$.unsubscribe();
    if (this.reuseTabChangeSubscription) {
      this.reuseTabChangeSubscription.unsubscribe();
    }
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
    }
  }

  get isCollapsed() {
    return this.settings.layout.collapsed;
  }

  get licSrv() {
    return this.injector.get(LicenseService);
  }

  showLicense() {
    if (this.licSrv && this.company.licensed) {
      this.licSrv.modalOpen.licenseInfo = true;
      this.licSrv.show(this.company.tenantId, this.company.ownerId);
    }
  }

  navigateToPlan() {
    const url = `/account/subscription/subscribe/${this.company['tenantId']}`;
    this.router.navigateByUrl(url);
  }

  gotoDashboard() {
    clearInterval(this.interval$);
  }

  loadLicenseText() {
    this.http.get('api/License/LicenseDisplay').subscribe({
      next: (data: any) => {
        this.licenseText = data.toString();
      }
    });
  }

  logout(): void {
    this.authService.logout();
  }

  addTab(reuseTabComponent: ReuseTabComponent, event: any) {
    reuseTabComponent.activate(event);
  }

  closeTab(event: any) {
    // When a tab is closed, remove its saved query so it doesn't persist
    const path =
      typeof event?.url === 'string'
        ? event.url.split('?')[0]
        : this.router.url.split('?')[0];
    // Using a symbol placed on the instance in ctor for the store
    const store: Map<string, string> | undefined = (this as any)._tabQueryStore;
    if (store) {
      store.delete(path);
    }
  }

  getScreenSize() {
    if (window.innerWidth < 736) this.isSmallScreen = true;
    else {
      this.isSmallScreen = false;
    }
  }
  lockApp() {
    try {
      const currentUrl = this.router.url;
      if (currentUrl && !currentUrl.startsWith('/passport')) {
        sessionStorage.setItem('lockReturnUrl', currentUrl);
      }
    } catch {
      // sessionStorage may be unavailable (e.g. private mode); ignore
    }
    this.router.navigateByUrl('/passport/lock');
    this.store.dispatch(toggleLockAction());
  }
}
